import { useCallback, useEffect, useState } from 'react';

import { useAccountContext } from 'utils/account/AccountContext';
import { SKYSPECS_ORG } from 'utils/constants';
import { flags, useFeatures } from 'utils/features';

import {
  getStoredDamages2PreLoadingPreference,
  resolveShouldDeferDamages2Query,
  setStoredDamages2PreLoadingPreference,
} from './damages2PreLoading';

export const useDamages2PreLoading = () => {
  const { customer, loading: accountLoading } = useAccountContext();
  const { features, loading: featuresLoading } = useFeatures();

  const customerId = customer?.id;
  // Flag name is fixed backend-side: ON means default to defer (No Pre-Loading).
  const orgFeatureFlagEnabled = Boolean(features?.[flags.DAMAGES_2_NO_PRE_LOADING]);
  const isSkySpecsOrg = customerId === SKYSPECS_ORG;
  const isAccountReady = !accountLoading && !featuresLoading && !!customerId;

  // localStorage is sync — hydrate on first paint so an opted-out user does not
  // briefly resolve to the org-flag default (banner flash / skipped query).
  const [storedPreference, setStoredPreference] = useState<boolean | null>(() =>
    getStoredDamages2PreLoadingPreference(customerId)
  );
  const [preferenceCustomerId, setPreferenceCustomerId] = useState(customerId);

  if (customerId !== preferenceCustomerId) {
    setPreferenceCustomerId(customerId);
    setStoredPreference(getStoredDamages2PreLoadingPreference(customerId));
  }

  // Per-org preference in local storage (namespaced key). Switching orgs must not
  // wipe other orgs. The org FF is a default only (`stored ?? flag`); do not seed
  // storage when missing, and do not clear a user override when the FF is off.
  useEffect(() => {
    if (!isAccountReady) {
      return;
    }

    if (isSkySpecsOrg) {
      setStoredPreference(null);
      return;
    }

    setStoredPreference(getStoredDamages2PreLoadingPreference(customerId));
  }, [customerId, isAccountReady, isSkySpecsOrg]);

  // SkySpecs always defers until a filter is applied, with no user toggle.
  // `true` = defer query (No Pre-Loading), never "pre-load is on".
  const shouldDeferUntilFiltered = isSkySpecsOrg
    ? true
    : resolveShouldDeferDamages2Query({ orgFeatureFlagEnabled, storedPreference });

  const setShouldDeferUntilFiltered = useCallback(
    (shouldDefer: boolean) => {
      if (!customerId || isSkySpecsOrg) {
        return;
      }

      setStoredDamages2PreLoadingPreference(customerId, shouldDefer);
      setStoredPreference(shouldDefer);
    },
    [customerId, isSkySpecsOrg]
  );

  return {
    isSkySpecsOrg,
    /**
     * SkySpecs has no user toggle. Every other org can opt in/out in Settings;
     * the org feature flag only supplies the default when nothing is stored.
     */
    canManageNoPreLoading: Boolean(customerId) && !isSkySpecsOrg,
    shouldDeferUntilFiltered,
    setShouldDeferUntilFiltered,
  };
};
